/**
 * El mapa de Gourmet Urbano.
 *
 * Ensambla: pide `nearby`, pinta los pines, distingue los tuyos, y decide
 * entre cuatro estados —cargando, con pines, vacío y fallo de carga.
 *
 * Lo que este mapa NO es: descubrimiento con ranking. `docs/10` define una
 * fórmula de tres capas con señales y penalizaciones; nada de eso entra
 * aquí. Este mapa pinta lo que `nearby` devuelve, que es proximidad pura
 * (spec §1).
 */

import {
  lazy,
  Suspense,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
} from 'react';

import { EmptyMap } from '../places/EmptyMap.js';
import { Boton } from '../../shared/ui/primitives.js';
import { fetchNearby, type NearbyPlace } from '../../shared/lib/api.js';
import { readPosition } from '../../shared/lib/geolocation.js';

/*
 * MapLibre entra por aquí y solo por aquí.
 *
 * ~275 KB gzip contra 93 KB de la app entera: sin el lazy, la landing de
 * invitación —que se abre en el webview de WhatsApp con datos móviles—
 * descargaría un motor de mapas para enseñar una tarjeta (spec §3.2).
 */
const MapCanvas = lazy(() => import('./MapCanvas.js'));

/** Centro de la zona seed: Roma/Condesa/Narvarte/Del Valle (ADR-004 §C7). */
const ZONA_SEED = { lat: 19.4, lng: -99.16 };

/** Lo que se pinta alrededor del usuario. */
const RADIO_MAPA_M = 5_000;

/**
 * El radio de la consulta "¿hay algo en la ciudad?".
 *
 * 20 000 m es `MAX_RADIUS_M` del servidor: pedir más lo recorta en
 * silencio. No es "toda la ciudad" en sentido estricto, pero cubre la ZMVM
 * desde cualquier punto del Valle, que es lo que la Era Pionera necesita.
 */
const RADIO_CIUDAD_M = 20_000;

/**
 * El nombre del basemap lleva la fecha del build de OSM del que se cortó
 * (ADR-019). Cambiarlo por uno nuevo es cambiar este string y subir el
 * archivo: no hay migración ni despliegue de código.
 *
 * **La URL es ABSOLUTA**, igual que el `sprite` y los `glyphs` de
 * `basemap.ts`. Las tiles no se piden desde el hilo principal sino desde
 * los workers de MapLibre, y ahí una ruta relativa no resuelve contra la
 * página. El síntoma es el mismo que ya costó una sesión: el mapa lee la
 * cabecera del archivo, resuelve la fuente, y luego no pide una sola
 * tile — lienzo vacío, sin error.
 */
export const PMTILES_URL = `${origenAssets()}/map/gu-zmvm-20260819.pmtiles`;

/**
 * El origen desde el que se sirven los assets del mapa.
 *
 * Duplica a propósito el helper de `basemap.ts` en vez de exportarlo: son
 * dos módulos con fronteras distintas y una función de cuatro líneas no
 * justifica acoplarlos.
 *
 * Bajo Node —los tests— `location` no existe y queda cadena vacía.
 */
function origenAssets(): string {
  return typeof location === 'undefined' ? '' : location.origin;
}

export interface MapScreenProps {
  onAddPlace: () => void;
  onOpenPlace: (placeId: string) => void;
}

export function MapScreen({ onAddPlace, onOpenPlace }: MapScreenProps) {
  const [centro, setCentro] = useState<{ lat: number; lng: number } | null>(null);
  const [places, setPlaces] = useState<NearbyPlace[] | null>(null);
  const [enLaCiudad, setEnLaCiudad] = useState(0);
  const [loadFailed, setLoadFailed] = useState(false);

  /*
   * El fallo del MAPA es distinto del fallo de los LUGARES, y por eso
   * lleva su propio estado.
   *
   * `loadFailed` es que `nearby` no respondió: sin datos no hay nada que
   * enseñar, y la pantalla entera se sustituye por el aviso con reintento.
   *
   * Esto otro es que el basemap no cargó —tiles, estilo, sprites— con los
   * datos ya en la mano. Los pines existen y `EmptyMap` sigue teniendo
   * sentido; lo que falta es el fondo. Reutilizar `loadFailed` aquí diría
   * "no pudimos traer los lugares" justo cuando SÍ los trajimos, que es
   * la misma mentira sobre el estado real que este archivo existe para
   * evitar, apuntando al otro lado.
   */
  const [falloMapa, setFalloMapa] = useState<string | null>(null);

  /*
   * El contador de intentos es la dependencia del efecto, y esa es toda la
   * mecánica del reintento: subirlo lo vuelve a correr.
   *
   * Se hace así y no con una función suelta que se llame a mano porque el
   * `return` del efecto —el que pone `vivo = false`— se ejecuta ANTES del
   * siguiente pase. O sea: React mismo se encarga de que la petición
   * anterior quede muerta antes de arrancar la nueva. Una función aparte
   * tendría que llevar esa contabilidad por su cuenta, y es justo donde
   * aparecen las respuestas viejas que pisan a las nuevas.
   */
  const [intento, setIntento] = useState(0);
  const [reintentando, setReintentando] = useState(false);

  useEffect(() => {
    let vivo = true;

    void (async () => {
      /*
       * Buscar DONDE ESTÁ el usuario, no en la zona piloto.
       *
       * Antes esto arrancaba con las coordenadas fijas de Roma/Condesa.
       * Alguien en Ajusco —a 13 km— agregaba su lugar, volvía al mapa y lo
       * veía vacío: su pin existía y estaba fuera del radio de búsqueda.
       * Pasó en la primera prueba con un teléfono real.
       *
       * Sin permiso de ubicación se cae a la zona piloto, que es donde va
       * a haber lugares primero — mejor eso que una pantalla en blanco.
       */
      const pos = await readPosition();
      const c = pos.ok ? { lat: pos.value.lat, lng: pos.value.lng } : ZONA_SEED;
      if (!vivo) return;
      setCentro(c);

      try {
        const res = await fetchNearby({ ...c, radiusM: RADIO_MAPA_M });
        if (!vivo) return;
        setPlaces(res.places);
        setLoadFailed(false);
        setReintentando(false);

        /*
         * La segunda consulta existe para NO MENTIR en el estado vacío.
         *
         * `EmptyMap` tiene dos variantes y elegir la primera cuando ya hay
         * lugares en la ciudad sería decir "abrimos en cero" a alguien que
         * solo está lejos. Solo se pide si el viewport salió vacío: en el
         * camino normal no cuesta nada.
         *
         * Su `try` es PROPIO, y eso no es un detalle de estilo.
         *
         * Compartiendo el `catch` de arriba, su fallo encendía `loadFailed`
         * y tiraba a la basura un `setPlaces` que YA había tenido éxito: el
         * mapa cargó bien, el viewport está vacío de verdad, y la pantalla
         * decía "no pudimos traer los lugares". Falso — sí pudimos.
         *
         * Esa consulta es puramente cosmética: elige entre dos redacciones.
         * Si falla, `enLaCiudad` se queda en 0 y se degrada a la variante A
         * de EmptyMap, que es exactamente lo que se veía antes de que esta
         * consulta existiera. Perder el mapa entero por una redacción sería
         * la mentira inversa a la que este archivo existe para impedir: un
         * vacío REAL disfrazado de error. Y se llevaría por delante la
         * pantalla que invita a marcar el primer lugar (ADR-007), justo en
         * la Era Pionera, cuando el vacío es el caso normal.
         */
        if (res.places.length === 0) {
          try {
            const ciudad = await fetchNearby({ ...c, radiusM: RADIO_CIUDAD_M });
            if (vivo) setEnLaCiudad(ciudad.places.length);
          } catch {
            // Sin dato, la variante A. El mapa no se toca.
          }
        }
      } catch {
        // Distinto de "no hay lugares": aquí la petición falló y el
        // usuario merece saberlo, no ver un vacío que parezca real.
        if (vivo) {
          setLoadFailed(true);
          // Se apaga también aquí: si no, un segundo fallo dejaría el botón
          // bloqueado en "Buscando…" para siempre y la pantalla volvería a
          // no tener salida, que es el bug que esto arregla.
          setReintentando(false);
        }
      }
    })();

    return () => {
      vivo = false;
    };
  }, [intento]);

  /*
   * `useMemo` y NO un `.map()` inline en el JSX.
   *
   * `MapCanvas` rehace TODOS los marcadores cuando cambia la identidad de
   * este array. Un `.map()` inline crea uno nuevo en cada render, así que
   * cada render destruiría y recrearía cada pin — y con ellos su nodo del
   * DOM, perdiendo un `:hover` o un pin a medio tocar.
   *
   * La dependencia es `places` (la respuesta de la API), que solo cambia
   * cuando llegan datos nuevos. Descubierto al implementar `MapCanvas`:
   * es un requisito del prop, no una optimización.
   */
  const puntos = useMemo(
    () =>
      (places ?? []).map((p) => ({
        id: p.id,
        lat: p.lat,
        lng: p.lng,
        // El nombre accesible del pin. `MapCanvas` lo exige y no puede
        // inventarlo: no sabe qué es un lugar. Sin esto un lector de
        // pantalla anunciaría "botón" una vez por pin (WCAG 4.1.2, A).
        etiqueta: p.name,
        destacado: p.isMine,
      })),
    [places],
  );

  const cargando = !loadFailed && (centro === null || places === null);

  if (loadFailed) {
    return (
      /*
       * `flex: 1` y NO el `AVISO` absoluto de los otros estados.
       *
       * `AVISO` es `position: absolute; inset: 0` para superponerse AL
       * MAPA, y funciona porque vive dentro del contenedor relativo de
       * abajo. Esta rama sale antes de ese contenedor, así que el `inset:
       * 0` se resolvía contra el viewport y el aviso tapaba la cabecera:
       * el logo y el avatar desaparecían justo en la pantalla donde el
       * usuario necesita ver que sigue dentro de la app. Medido en Chrome
       * — el `header` estaba en el DOM, debajo de un div de 756x413.
       */
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          alignContent: 'center',
          justifyItems: 'center',
          gap: 'var(--gu-space-5)',
          textAlign: 'center',
          background: 'var(--gu-surface-sunken)',
          padding: 'var(--gu-space-4)',
        }}
      >
        {/*
         * El copy ya no promete lo que no puede cumplir.
         *
         * Decía "No pudimos traer los lugares. Vuelve a intentar en un
         * momento" sin nada con qué intentarlo: el efecto corría una vez y
         * la única salida era recargar la app. Ahora la frase se queda en
         * el hecho y la acción la lleva el botón, que es donde se puede
         * tocar.
         */}
        <p style={{ margin: 0, color: 'var(--gu-text-secondary)' }}>
          No pudimos traer los lugares.
        </p>

        {/*
         * El `Boton` compartido y no un `<button>` con estilos propios:
         * `primitives.tsx` existe justo porque "cada pantalla llevaba sus
         * propios `style={{}}`, así que una corrección de espaciado había
         * que repetirla seis veces". Ya trae el `--gu-primary-strong` que
         * pasa AA y el área táctil de 44 px.
         *
         * `ancho={false}`: es un aviso centrado, no el pie de un
         * formulario, y un botón a todo lo ancho del mapa pesaría más que
         * el mensaje.
         */}
        <Boton
          onClick={() => {
            setReintentando(true);
            setIntento((n) => n + 1);
          }}
          disabled={reintentando}
          ancho={false}
        >
          {reintentando ? 'Buscando…' : 'Reintentar'}
        </Boton>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', flex: 1, minHeight: 0 }}>
      {cargando && (
        <div style={AVISO}>
          <p style={{ margin: 0, color: 'var(--gu-text-secondary)' }}>Cargando…</p>
        </div>
      )}

      {centro && (
        /*
         * El fallback DICE algo. Era un div gris sin texto, y no es un
         * hueco teórico: `cargando` se apaga cuando llegan `centro` y
         * `places`, pero el `import()` del lazy ni siquiera arranca hasta
         * que hay `centro`. El chunk son ~257 KB gzip —bastante más lento
         * que `fetchNearby` con datos móviles— así que entre un momento y
         * otro se miraba un rectángulo mudo.
         *
         * Dice "Cargando el mapa…" y no "Cargando…" a secas: es la
         * descarga del motor, no la de los lugares, y quien esté en una
         * red lenta merece saber que lo que tarda es grande y viene una
         * sola vez.
         */
        <Suspense
          fallback={
            <div style={AVISO}>
              <p style={{ margin: 0, color: 'var(--gu-text-secondary)' }}>
                Cargando el mapa…
              </p>
            </div>
          }
        >
          <MapCanvas
            pmtilesUrl={PMTILES_URL}
            centro={centro}
            puntos={puntos}
            onPuntoClick={onOpenPlace}
            /*
             * Sin esto, un basemap roto es INVISIBLE.
             *
             * MapLibre no lanza: emite en su evento `error` y sigue
             * adelante con el lienzo vacío. Cuando el `sprite` era una
             * ruta relativa —MapLibre 6 las rechaza— el estilo se abortaba
             * entero y el mapa salía en blanco sin una sola línea en
             * consola. El fallo llegó a producción y se diagnosticó por el
             * síntoma, no por el mensaje.
             */
            onError={(motivo) => setFalloMapa(motivo)}
          />
        </Suspense>
      )}

      {/*
       * El aviso de basemap roto: una banda, no una pantalla.
       *
       * Va ARRIBA y ocupa poco a propósito. Los pines y `EmptyMap` siguen
       * siendo útiles sin fondo —se puede tocar un lugar y llegar a su
       * ficha—, así que tapar la pantalla costaría más de lo que informa.
       *
       * Y dice qué se puede hacer. "El fondo del mapa no cargó" a secas
       * deja a alguien mirando una pantalla que no entiende; nombrar que
       * los lugares SÍ están convierte un fallo en una limitación.
       */}
      {falloMapa && (
        <div
          role="status"
          style={{
            position: 'absolute',
            insetInline: 0,
            top: 0,
            zIndex: 'var(--gu-z-sheet)' as unknown as number,
            background: 'var(--gu-surface-raised)',
            borderBottom: '1px solid var(--gu-border-default)',
            padding: 'var(--gu-space-3) var(--gu-space-4)',
            fontSize: 'var(--gu-text-caption)',
            color: 'var(--gu-text-secondary)',
            textAlign: 'center',
          }}
        >
          El fondo del mapa no cargó. Tus lugares siguen aquí: tócalos para
          abrirlos.
          {/*
           * El motivo, en pequeño y monoespaciado.
           *
           * No es un mensaje "para el usuario" y no pretende serlo: es
           * para que quien tenga el fallo delante pueda leerlo y
           * mandarlo. Un basemap roto costó una sesión entera de
           * depuración a ciegas justamente porque este texto no existía
           * en ninguna parte.
           */}
          <span
            style={{
              display: 'block',
              marginTop: 'var(--gu-space-1)',
              fontFamily: 'ui-monospace, Menlo, monospace',
              fontSize: '0.7rem',
              color: 'var(--gu-text-tertiary)',
              wordBreak: 'break-word',
            }}
          >
            {falloMapa}
          </span>
        </div>
      )}

      {places && places.length === 0 && (
        <EmptyMap cityPlaceCount={enLaCiudad} onAddPlace={onAddPlace} />
      )}
    </div>
  );
}

/** Se superpone AL MAPA: vive dentro del contenedor relativo de abajo. */
const AVISO: CSSProperties = {
  position: 'absolute',
  inset: 0,
  display: 'grid',
  placeItems: 'center',
  background: 'var(--gu-surface-sunken)',
  padding: 'var(--gu-space-4)',
};

